import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Alert, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import {
    usePauseEngageActionPlan,
    useResumeEngageActionPlan,
    useTimelineActionPlan,
} from '../../../sic-lib/api/hooks/useEngageActionPlan';
import { ActionPlanStatus } from '../../../sic-lib/enums/EngageActionPlansEnums';
import ButtonLoading from '../../Shared/ButtonLoading';
import DisplayDateTime from '../../Shared/DisplayDateTime/DisplayDateTime';
import Loader from '../../Shared/Loader';
import AutomationDialogTimelineItem from './AutomationDialogTimelineItem';

interface Props {
    contactId: number;
    selectedId: number | undefined;
}

export default function AutomationDialogTimeline({ contactId, selectedId }: Props) {
    const {
        data: timelineData,
        refetch: refetchTimeline,
        isPending: isPlanLoading,
    } = useTimelineActionPlan(contactId, selectedId);

    const { mutate: pausePlan, isPending: isPauseLoading } = usePauseEngageActionPlan(contactId);
    const { mutate: resumePlan, isPending: isResumeLoading } = useResumeEngageActionPlan(contactId);

    useEffect(() => {
        if (selectedId) {
            refetchTimeline();
        }
    }, [selectedId]);

    const handlePauseResume = () => {
        if (timelineData) {
            if (timelineData?.status === ActionPlanStatus.Paused) {
                resumePlan(timelineData);
            } else {
                pausePlan(timelineData);
            }
        }
    };

    if (!selectedId || !timelineData) {
        return <Alert severity="info">Please select an action plan to view the timeline.</Alert>;
    }

    return (
        <Loader isLoading={isPlanLoading}>
            <Stack direction="column" justifyContent="flex-start" display="flex" gap={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" gap={2}>
                        <Typography variant="subtitle2">{timelineData?.description}</Typography>
                        <Typography variant="caption">{timelineData?.status}</Typography>
                        {timelineData?.status === ActionPlanStatus.Completed && (
                            <DisplayDateTime
                                dateText={timelineData?.completeDate}
                                // variant="labelMedium"
                                variant="caption"
                            />
                        )}
                        {timelineData?.status === ActionPlanStatus.Paused && (
                            <Typography variant="caption">
                                <DisplayDateTime
                                    dateText={timelineData.pausedDate}
                                    variant="caption"
                                />
                                {' by '}
                                {timelineData?.pausedBy}
                            </Typography>
                        )}
                    </Stack>
                    <Stack>
                        {timelineData?.status !== ActionPlanStatus.Completed && (
                            <ButtonLoading
                                variant="contained"
                                onClick={handlePauseResume}
                                isLoading={isResumeLoading || isPauseLoading}
                                startIcon={
                                    timelineData?.status === ActionPlanStatus.Paused ? (
                                        <PlayArrowIcon />
                                    ) : (
                                        <PauseIcon />
                                    )
                                }
                                color={
                                    timelineData?.status === ActionPlanStatus.Paused
                                        ? 'success'
                                        : 'warning'
                                }
                            >
                                {timelineData?.status === ActionPlanStatus.Paused
                                    ? 'Resume'
                                    : 'Pause'}
                            </ButtonLoading>
                        )}
                    </Stack>
                </Stack>
                <AutomationDialogTimelineItem timelineData={timelineData?.actions} />
            </Stack>
        </Loader>
    );
}
